/**
 * Visual Harmony Utility
 * Ensures color contrast and harmonized gradients for Smart Swirls
 */

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex) {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return { r, g, b };
}

/**
 * Calculate relative luminance (for WCAG contrast)
 */
function getLuminance(rgb) {
  const [r, g, b] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map(val => {
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.07152 * g + 0.0722 * b;
}

/**
 * Calculate contrast ratio between two colors
 */
function getContrastRatio(color1, color2) {
  const lum1 = getLuminance(hexToRgb(color1));
  const lum2 = getLuminance(hexToRgb(color2));
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Adjust color brightness
 */
function adjustBrightness(hex, percent) {
  const rgb = hexToRgb(hex);
  const factor = percent / 100;
  
  return {
    r: Math.round(Math.max(0, Math.min(255, rgb.r + (255 * factor)))),
    g: Math.round(Math.max(0, Math.min(255, rgb.g + (255 * factor)))),
    b: Math.round(Math.max(0, Math.min(255, rgb.b + (255 * factor))))
  };
}

/**
 * Convert RGB to hex
 */
function rgbToHex(rgb) {
  return `#${[rgb.r, rgb.g, rgb.b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('')}`;
}

/**
 * Ensure color palette has sufficient contrast (≥ 4.5 AA for normal text)
 * Adjusts colors if needed to meet accessibility standards
 */
export function harmonizeColorPalette(colorPalette, textColor = '#1A1A1A') {
  if (!colorPalette) {
    return {
      primary: '#A78BFA',
      gradient: ['#A78BFA', '#C084FC']
    };
  }

  let primary = colorPalette.primary || colorPalette.gradient?.[0] || '#A78BFA';
  let gradient = Array.isArray(colorPalette.gradient) && colorPalette.gradient.length >= 2
    ? [...colorPalette.gradient]
    : [primary, colorPalette.primary || '#C084FC'];

  // Check contrast ratio against text color
  const minContrast = 4.5;
  let adjustedPrimary = primary;
  let adjustedGradient = [...gradient];

  // Adjust primary color if contrast is too low
  let contrast = getContrastRatio(primary, textColor);
  if (contrast < minContrast) {
    // Darken or lighten based on luminance
    const primaryLum = getLuminance(hexToRgb(primary));
    const textLum = getLuminance(hexToRgb(textColor));
    
    // If primary is darker than text, lighten it; otherwise darken
    const adjustment = primaryLum < textLum ? 30 : -20;
    const adjustedRgb = adjustBrightness(primary, adjustment);
    adjustedPrimary = rgbToHex(adjustedRgb);
    
    // Verify new contrast
    contrast = getContrastRatio(adjustedPrimary, textColor);
    if (contrast < minContrast) {
      // More aggressive adjustment
      const moreAdjustment = primaryLum < textLum ? 50 : -40;
      const moreAdjustedRgb = adjustBrightness(primary, moreAdjustment);
      adjustedPrimary = rgbToHex(moreAdjustedRgb);
    }
  }

  // Adjust gradient colors similarly
  adjustedGradient = adjustedGradient.map((color, index) => {
    const gradContrast = getContrastRatio(color, textColor);
    if (gradContrast < minContrast) {
      const gradLum = getLuminance(hexToRgb(color));
      const textLum = getLuminance(hexToRgb(textColor));
      const adjustment = gradLum < textLum ? 25 : -15;
      const adjustedRgb = adjustBrightness(color, adjustment);
      return rgbToHex(adjustedRgb);
    }
    return color;
  });

  return {
    primary: adjustedPrimary,
    gradient: adjustedGradient
  };
}

/**
 * Generate harmonious gradient stops for CSS
 */
export function generateGradientCSS(colors, direction = '135deg') {
  if (!colors || !Array.isArray(colors) || colors.length === 0) {
    return `linear-gradient(${direction}, #A78BFA, #C084FC)`;
  }
  
  if (colors.length === 1) {
    return `linear-gradient(${direction}, ${colors[0]}, ${colors[0]})`;
  }
  
  return `linear-gradient(${direction}, ${colors.join(', ')})`;
}

